import React, { MouseEvent } from 'react';
import _startCase from 'lodash/startCase';
import { useIntl } from '@kyruus/intl';
import { visuallyHidden } from '@biome-design-system/styles';
import { Box } from '@biome-design-system/ui/Box';
import { Paper } from '@biome-design-system/ui/Paper';
import { Typography } from '@biome-design-system/ui/Typography';
import { IconButton } from '@biome-design-system/ui/IconButton';
import { CloseIcon } from '@biome-design-system/icons/CloseIcon';

import type {
  Log,
  SearchSummary,
  SearchModification,
  SearchFacetWithAppliedTerms
} from 'Src/types';
import { BasicContent } from './BasicContent';
import { LocationFilter } from '../../../Filters/LocationFilter';
import { TypeaheadFilter } from '../../../Filters/TypeaheadFilter';
import { messages } from '../../../messages';
import { kebabCase } from 'lodash';

interface ContentProps {
  searchSummary: SearchSummary;
  facet: SearchFacetWithAppliedTerms;
  updateSearch: (modifications: SearchModification[]) => void;
  log: Log;
  handleClose: (event: MouseEvent, reason: 'closeButton') => void;
}

export const Content = ({
  searchSummary,
  facet,
  updateSearch,
  log,
  handleClose
}: ContentProps) => {
  const intl = useIntl();
  const facetLabelMessageDescriptor = {
    id: `field.name.${facet.field}`,
    defaultMessage: _startCase(facet.field),
    description: 'The name displayed above the facet'
  };
  const facetIsComposite = !!facet && !!facet?.composite;

  const facetLabelId = `${kebabCase(facet.field)}-label`;
  let filterContent: React.ReactNode;
  switch (facet.type) {
    case 'location':
      filterContent = (
        <LocationFilter
          locationFacet={facet}
          searchSummary={searchSummary}
          updateSearch={updateSearch}
          log={log}
        />
      );
      break;
    case 'typeahead':
      filterContent = (
        <TypeaheadFilter
          facet={facet}
          labelId={facetLabelId}
          hideCount={facetIsComposite}
          updateSearch={updateSearch}
        />
      );
      break;
    default:
      filterContent = (
        <BasicContent
          facet={facet}
          hideCount={facetIsComposite}
          updateSearch={updateSearch}
          labelName={facetLabelMessageDescriptor}
        />
      );
  }

  return (
    <Paper
      component="fieldset"
      sx={{
        display: 'grid',
        width: 360,
        m: 0,
        p: 0,
        border: 'none',
        gridTemplateColumns: '1fr auto',
        gridTemplateAreas: `
          'title close-btn'
          'content content'
        `
      }}
    >
      <Box component="legend" sx={visuallyHidden} id={facetLabelId}>
        {intl.formatMessage(facetLabelMessageDescriptor)}
      </Box>
      <Typography
        aria-hidden={true}
        sx={{
          fontWeight: 'bold',
          gridArea: 'title',
          mt: 3,
          mx: 3
        }}
        variant="h5"
        component="span"
      >
        {intl.formatMessage(facetLabelMessageDescriptor)}
      </Typography>
      <IconButton
        sx={{ gridArea: 'close-btn', m: 2 }}
        data-testid="close-filter-popover"
        aria-label={intl.formatMessage(messages.closeFilterPopoverAriaLabel)}
        title={intl.formatMessage(messages.closeFilterPopoverAriaLabel)}
        onClick={(e: MouseEvent) => {
          handleClose(e, 'closeButton');
        }}
      >
        <CloseIcon color="inherit" fontSize="medium" />
      </IconButton>
      <Box sx={{ gridArea: 'content', mx: 3, mb: 3 }}>{filterContent}</Box>
    </Paper>
  );
};
